import { omit, pick } from 'lodash';
import cloneDeep from 'lodash/cloneDeep';

import { Response } from '@luxuryescapes/contract-svc-tour';

export interface TourOptionsFormData {
  base: Response.TourOptionsFormResponse;
  derived: Response.TourOptionsFormResponse;
}

export function prepareTourOptionsForDisplay(tourOptionsSchema: Response.TourOptionsFormResponse): TourOptionsFormData {
  const base = cloneDeep(tourOptionsSchema);
  const schema = cloneDeep(base.schema);
  const uiSchema = cloneDeep(base.uiSchema);
  const formData = cloneDeep(base.formData);

  if (schema.properties?.tourOptions?.items?.properties) {
    schema.properties.tourOptions.items.properties = omit(schema.properties.tourOptions.items.properties, [
      'itinerary',
      'itineraryTransferInformation',
    ]);
  }

  if (uiSchema.tourOptions?.items) {
    uiSchema.tourOptions.items = omit(uiSchema.tourOptions.items, ['itinerary', 'itineraryTransferInformation']);
  }
  return {
    base,
    derived: {
      schema,
      uiSchema,
      formData,
    },
  };
}

type DerivedSchema = {
  schema: {
    properties: {
      tourOptionItinerary: {
        items: {
          properties: {
            day: {
              items: {
                properties: {
                  specialCallOuts: {
                    items: {
                      properties: {
                        svcImageId: {
                          $ref: string;
                        };
                      };
                    };
                  };
                };
              };
            };
          };
        };
      };
    };
  };
  uiSchema: {
    tourOptionItinerary: {
      items: {
        day: unknown;
      };
    };
  };
  formData: {
    tourOptionItinerary: Array<{
      day: unknown;
      tourOptionKey: string;
      itineraryTransferInformation?: ItineraryTransferInformation;
      [key: string]: unknown;
    }>;
  };
};

function getDerivedSchema({ schema, uiSchema, formData }: Response.TourOptionsFormResponse): DerivedSchema {
  const derivedSchema: Response.TourOptionsFormResponse['schema'] & Partial<DerivedSchema['schema']> =
    cloneDeep(schema);
  derivedSchema.properties.tourOptionItinerary = cloneDeep(schema.properties.tourOptions);
  delete derivedSchema.properties.tourOptions;
  derivedSchema.properties.tourOptionItinerary.items.properties.day =
    derivedSchema.properties.tourOptionItinerary.items.properties.itinerary;
  delete derivedSchema.properties.tourOptionItinerary.items.properties.itinerary;
  derivedSchema.properties.tourOptionItinerary.items.properties.day.items.properties.specialCallOuts.items.properties.svcImageId[
    '$ref'
  ] = '#/properties/tourOptionItinerary/definitions/hostsImages';
  const airports = derivedSchema.properties.tourOptionItinerary.items.properties.itineraryTransferInformation;
  if (airports) {
    delete derivedSchema.properties.tourOptionItinerary.items.properties.itineraryTransferInformation;
    derivedSchema.properties.tourOptionItinerary.items.properties.itineraryTransferInformation = airports;
  }
  const derivedUiSchema: Response.TourOptionsFormResponse['uiSchema'] & Partial<DerivedSchema['uiSchema']> =
    cloneDeep(uiSchema);
  derivedUiSchema.tourOptionItinerary = derivedUiSchema.tourOptions;
  delete derivedUiSchema.tourOptions;
  derivedUiSchema.tourOptionItinerary.items.day = derivedUiSchema.tourOptionItinerary.items.itinerary;
  delete derivedUiSchema.tourOptionItinerary.items.itinerary;
  const airportsUi = derivedUiSchema.tourOptionItinerary.items.itineraryTransferInformation;
  if (airportsUi) {
    delete derivedUiSchema.tourOptionItinerary.items.itineraryTransferInformation;
    derivedUiSchema.tourOptionItinerary.items.itineraryTransferInformation = airportsUi;
  }
  const derivedFormData: Response.TourOptionsFormResponse['formData'] & Partial<DerivedSchema['formData']> =
    cloneDeep(formData);
  derivedFormData.tourOptionItinerary = derivedFormData.tourOptions.map((tourOption) => ({
    ...tourOption,
    day: tourOption.itinerary,
  }));
  delete formData.tourOptions;
  return {
    schema: derivedSchema as DerivedSchema['schema'],
    uiSchema: derivedUiSchema as DerivedSchema['uiSchema'],
    formData: derivedFormData as DerivedSchema['formData'],
  };
}

export type TourOptionItineraryFormData = {
  base: Response.TourOptionsFormResponse;
  derived: DerivedSchema;
};

export function extractItineraryFormData(
  tourOptionsSchema: Response.TourOptionsFormResponse,
): TourOptionItineraryFormData {
  const base = cloneDeep(tourOptionsSchema);
  const schema = cloneDeep(base.schema);
  const uiSchema = cloneDeep(base.uiSchema);
  const formData = cloneDeep(base.formData);

  if (schema.properties?.tourOptions?.items?.properties) {
    const schemaProperties = pick(schema.properties.tourOptions.items.properties, [
      'id',
      'itinerary',
      'tourOptionKey',
      'itineraryTransferInformation',
    ]);
    schemaProperties.tourOptionKey.title = 'Title';
    schema.properties.tourOptions.items.properties = {
      tourOptionKey: schemaProperties.tourOptionKey,
      itinerary: schemaProperties.itinerary,
      itineraryTransferInformation: schemaProperties.itineraryTransferInformation,
    };
    schema.properties.tourOptions.title = 'Tour Option Itineraries';
    if (schema.properties.tourOptions.items.properties.itinerary?.items) {
      schema.properties.tourOptions.items.properties.itinerary.title = 'Days';
      if (schema.properties.tourOptions.items.properties.itinerary.items?.properties?.title) {
        schema.properties.tourOptions.items.properties.itinerary.items.properties.title.title = 'Title';
      }
    }
  }

  if (uiSchema.tourOptions?.items?.itinerary) {
    uiSchema.tourOptions.items = pick(uiSchema.tourOptions.items, ['id', 'itinerary', 'itineraryTransferInformation']);
    uiSchema.tourOptions.items.tourOptionKey = {
      'ui:widget': 'text',
    };
  }
  if (uiSchema.tourOptions?.['ui:options']) {
    uiSchema.tourOptions['ui:options'].itemTitleProperty = 'tourOptionKey';
    uiSchema.tourOptions['ui:options'].removable = true;
  }
  if (uiSchema.tourOptions?.items?.itinerary?.['ui:options']) {
    uiSchema.tourOptions.items.itinerary['ui:options'].orderable = true;
  }

  formData.tourOptions = formData.tourOptions?.map(
    (tourOption: Response.TourOptionsFormResponse['formData']['tourOptions'][number]) =>
      pick(tourOption, ['id', 'itinerary', 'tourOptionKey', 'itineraryTransferInformation']),
  ) as Response.TourOptionsFormResponse['formData']['tourOptions'];

  return {
    base,
    derived: getDerivedSchema({ schema, uiSchema, formData }),
  };
}

export type ItineraryTransferInformation = {
  departureAirport: string | null;
  arrivalAirport: string | null;
  arrivalTransferType?: string | null;
  departureTransferType?: string | null;
};

export type TourOptionItinerary = {
  day: Array<Response.TourOptionsFormResponse['formData']['tourOptions'][number]['itinerary'][number]>;
  tourOptionKey: string;
  itineraryTransferInformation?: ItineraryTransferInformation;
  [key: string]: unknown;
};

export type ItineraryByDepartureFormData = Omit<Response.TourOptionsFormResponse['formData'], 'tourOptions'> & {
  tourOptionItinerary: Array<TourOptionItinerary & { hasBeenCloned?: boolean }>;
};

export function packageItineraryFormData(
  baseFormData: Response.TourOptionsFormResponse['formData'],
  formData: ItineraryByDepartureFormData,
): Response.TourOptionsFormResponse['formData'] {
  const base = cloneDeep(baseFormData);
  if (Array.isArray(base?.tourOptions)) {
    base.tourOptions.forEach((tourOption: Response.TourOptionsFormResponse['formData']['tourOptions'][number]) => {
      const matching = formData.tourOptionItinerary.find((to) => to.id === tourOption.id && !('departureId' in to));
      if (matching) {
        Object.assign(tourOption, {
          itinerary: matching.day,
          itineraryTransferInformation: matching.itineraryTransferInformation,
        });
      }
    });
  }
  return base;
}
